import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Download, ExternalLink, FileText, Check } from "lucide-react";

const PDF_PATH = "/lighting/hospitality-hub-lighting-catalogue-2026.pdf";
const COVER_PATH = "/lighting/catalogue-cover-01.jpg";
const HERO_PATH = "/lighting/lighting-hero.jpg";
const CATALOGUE_TITLE = "HOSPITALITY HUB: LIGHTING CATALOGUE 2026";
const CATALOGUE_PAGES = 63;
const CATALOGUE_DOWNLOAD_NAME = "Hospitality Hub - Lighting Catalogue 2026.pdf";

const LightingPage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "Lighting | Hospitality Hub";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <main data-testid="lighting-page">
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[52vh] min-h-[420px] md:h-[600px] w-full overflow-hidden bg-[#1A1A1A]">
          <img
            src={HERO_PATH}
            alt="Hospitality Hub Lighting"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/85 via-[#1A1A1A]/45 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full pb-14 md:pb-20">
              <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 mb-4">
                <Link to="/" className="hover:text-[#D4AF37]" data-testid="lighting-breadcrumb-home">
                  Home
                </Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-[#D4AF37]">Lighting</span>
              </nav>
              <p className="text-[10px] md:text-xs uppercase tracking-[0.32em] text-[#D4AF37] mb-3">
                Hospitality Lighting Division
              </p>
              <h1
                data-testid="lighting-title"
                className="font-serif-display text-5xl lg:text-7xl text-white max-w-4xl leading-[1.05]"
              >
                Hospitality lighting for guestroom, interior &amp; exterior.
              </h1>
              <p className="text-white/85 mt-5 max-w-2xl text-base lg:text-lg leading-relaxed">
                Complete interior, exterior and guestroom lighting solutions for hotels, developers,
                procurement teams, interior designers and renovation projects, at factory-direct pricing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CATALOGUE SECTION */}
      <section id="catalogue" data-testid="lighting-catalogue-section" className="bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-3xl mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Lighting Catalogue</p>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-tight">
              The complete Hospitality Hub lighting catalogue.
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-2xl">
              Explore our complete 2026 lighting catalogue, featuring interior, exterior, guestroom and
              hospitality lighting solutions designed for hotel environments.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 items-center">
            {/* Cover preview */}
            <div className="lg:col-span-6">
              <div className="relative group">
                <div className="absolute -inset-4 bg-[#D4AF37]/10 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity" />
                <a
                  href={PDF_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lighting-catalogue-cover-link"
                  className="relative block bg-white shadow-[0_30px_80px_rgb(0,0,0,0.5)] hover:shadow-[0_40px_100px_rgb(0,0,0,0.7)] transition-all duration-500 border border-white/10"
                >
                  <img
                    src={COVER_PATH}
                    alt={CATALOGUE_TITLE}
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-[#D4AF37] text-white text-[10px] uppercase tracking-[0.28em] px-3 py-1.5">
                    Official Catalogue
                  </div>
                </a>
              </div>
            </div>

            {/* Details + CTAs */}
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-[#D4AF37] mb-4">
                <FileText className="w-4 h-4" />
                PDF · {CATALOGUE_PAGES} pages · 2026 Edition
              </div>
              <h3
                data-testid="lighting-catalogue-title"
                className="font-serif-display text-3xl md:text-4xl leading-tight text-white"
              >
                {CATALOGUE_TITLE}
              </h3>
              <p className="mt-5 text-white/70 leading-relaxed">
                The authoritative reference for our full hospitality lighting portfolio, including LED bulbs &amp;
                fixtures, tubes, panels &amp; troffers, downlights, wallpacks, flood, bollard and canopy
                lights, garden and post lights, guestroom sconces, vanity strips, LED mirrors, pendants,
                ceiling lights, lamp collections and hotel accessories including TV mounts, exit signs
                and shades.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={PDF_PATH}
                  download={CATALOGUE_DOWNLOAD_NAME}
                  data-testid="lighting-catalogue-download-btn"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B5952F] text-white px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
                >
                  <Download className="w-4 h-4" /> Download Catalogue
                </a>
                <a
                  href={PDF_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lighting-catalogue-view-btn"
                  className="inline-flex items-center gap-2 border border-white/50 hover:bg-white hover:text-[#1A1A1A] text-white px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
                >
                  <ExternalLink className="w-4 h-4" /> View Catalogue
                </a>
              </div>

              <ul className="mt-10 grid grid-cols-2 gap-x-6 gap-y-3 text-xs text-white/60 uppercase tracking-[0.16em] border-t border-white/10 pt-6">
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Interior &amp; Exterior</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> CCT / Wattage / Lumens</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Brand-Approved</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Factory-Direct</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LightingPage;
